/**
 * Partner Create Page
 *
 * Create a new partner configuration with all required fields.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import { Input, Select, TextArea } from '@/components/common/FormField';
import {
  CreatePartnerRequest,
  DefaultOfferConfig,
  PartnerConfigJson,
  PartnerType,
  PARTNER_TYPE_OPTIONS,
  PartnerResponse,
  TIMEZONE_OPTIONS,
  localDatetimeToUtcIso,
} from '@/app/types/partners';
import { partnerService } from '@/app/services/partnerService';
import { auditService } from '@/app/services';
import { DefaultOffersEditor } from '@/components/partners/DefaultOffersEditor';
import { usePartnerData } from '@/app/hooks/usePartnerData';

import './PartnerCreate.scss';

export const PartnerCreate: React.FC = () => {
  const navigate = useNavigate();
  const { invalidate, refresh } = usePartnerData();

  // Form state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreatePartnerRequest>({
    partner_product_id: 0,
    partner_name: '',
    doing_business_as: '',
    partner_type: 'EMPLOYER',
    effective_date: new Date().toISOString().split('T')[0] as string,
    is_active: true,
  });

  // Copy-from feature state
  const [availablePartners, setAvailablePartners] = useState<PartnerResponse[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [selectedCopyFrom, setSelectedCopyFrom] = useState<string>('');
  const [schedActivationLocal, setSchedActivationLocal] = useState('');
  const [schedActivationTimezone, setSchedActivationTimezone] = useState(
    TIMEZONE_OPTIONS[0]?.value ?? 'America/New_York'
  );

  // Fetch available partners for copy-from dropdown
  useEffect(() => {
    const fetchPartners = async () => {
      setLoadingPartners(true);
      try {
        // Fetch all partners (up to 1000 limit) - no filter by is_active to include both active and inactive
        const response = await partnerService.queryPartners({ limit: 1000 });
        setAvailablePartners(response.partners);
      } catch (err) {
        logger.error('Failed to fetch partners for copy-from', { error: err });
      } finally {
        setLoadingPartners(false);
      }
    };
    fetchPartners();
  }, []);

  // Handle copying from an existing partner
  const handleCopyFrom = useCallback(async (partnerProductId: string) => {
    if (!partnerProductId) {
      setSelectedCopyFrom('');
      return;
    }

    setSelectedCopyFrom(partnerProductId);
    try {
      const partnerId = parseInt(partnerProductId, 10);
      const partner = await partnerService.getPartnerById(partnerId);
      if (!partner) {
        setError('Failed to load partner for copying');
        return;
      }

      // Copy all fields except those that should be unique or system-managed
      const copiedData: CreatePartnerRequest = {
        partner_product_id: 0, // User must provide a new unique ID
        partner_id: null, // Optional, user provides if needed
        partner_name: partner.partner_name,
        doing_business_as: partner.doing_business_as,
        partner_type: partner.partner_type,
        program_code: partner.program_code ?? null,
        package_id: partner.package_id ?? null,
        purse_name: partner.purse_name ?? null,
        earned_benefits: partner.earned_benefits,
        default_offers: partner.default_offers,
        content_overrides: partner.content_overrides,
        effective_date: new Date().toISOString().split('T')[0] as string, // Set to today
        expiration_date: partner.expiration_date,
        is_active: partner.is_active,
        notes: partner.notes,
        config: partner.config ?? null,
      };

      setFormData(copiedData);
      setSchedActivationLocal('');
      setSchedActivationTimezone(TIMEZONE_OPTIONS[0]?.value ?? 'America/New_York');
      setError(null);
    } catch (err) {
      setError('Failed to copy partner configuration');
      logger.error('Failed to copy partner', { error: err });
    }
  }, []);

  // Handle form field changes
  const handleChange = <K extends keyof CreatePartnerRequest>(
    field: K,
    value: CreatePartnerRequest[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle default offers changes
  const handleDefaultOffersChange = (offers: DefaultOfferConfig[] | null) => {
    setFormData((prev) => ({ ...prev, default_offers: offers }));
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!formData.partner_product_id || formData.partner_product_id <= 0) {
      return 'Partner Product ID is required and must be greater than 0';
    }
    if (!formData.partner_name.trim()) {
      return 'Partner Name is required';
    }
    if (!formData.doing_business_as.trim()) {
      return 'Doing Business As is required';
    }
    if (!formData.partner_type) {
      return 'Partner Type is required';
    }
    if (!formData.effective_date) {
      return 'Effective Date is required';
    }
    return null;
  };

  // Create partner
  const handleCreate = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const schedulePayload =
        formData.is_active === false && schedActivationLocal.trim()
          ? {
              scheduled_activation_at: localDatetimeToUtcIso(
                schedActivationLocal,
                schedActivationTimezone
              ),
              activation_timezone: schedActivationTimezone,
            }
          : { scheduled_activation_at: null, activation_timezone: null };

      const created = await partnerService.createPartner({
        ...formData,
        ...schedulePayload,
      });

      // Log record creation for audit
      auditService
        .logRecordModification('created', 'partner', String(created.partner_product_id), {
          partner_name: created.partner_name,
          partner_type: created.partner_type,
        })
        .catch((err) => logger.warn('Failed to log record creation', { error: err }));

      invalidate();
      refresh(); // Force immediate refetch
      navigate(`/partners/${created.partner_product_id}`);
    } catch (err) {
      setError('Failed to create partner');
      logger.error('Failed to create partner', { error: err });
    } finally {
      setSaving(false);
    }
  };

  // Cancel creation
  const handleCancel = () => {
    navigate('/partners');
  };

  return (
    <div className="partner-create-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={handleCancel} className="btn-back-link">
          ← Cancel and go back
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-actions">
          <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn-create" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating...' : 'Create Partner'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {/* Copy From Existing Partner */}
      <Card className="form-card copy-from-card">
        <CardHeader
          title="Copy from Existing Partner"
          subtitle="Optional: Start with an existing partner configuration"
        />
        <CardBody>
          <Select
            label="Select Partner to Copy"
            value={selectedCopyFrom}
            onChange={handleCopyFrom}
            options={[
              { value: '', label: 'Start from scratch' },
              ...availablePartners
                .sort((a, b) =>
                  (a.doing_business_as || a.partner_name).localeCompare(
                    b.doing_business_as || b.partner_name
                  )
                )
                .map((p) => ({
                  value: p.partner_product_id.toString(),
                  label: `${p.doing_business_as || p.partner_name || 'Partner'} (${p.partner_product_id})`,
                })),
            ]}
            disabled={loadingPartners}
            hint={
              selectedCopyFrom
                ? 'Configuration copied! Update the Partner Product ID to create a new partner.'
                : 'Select an existing partner to copy its configuration as a starting point.'
            }
          />
        </CardBody>
      </Card>

      {/* Basic Information */}
      <Card className="form-card">
        <CardHeader title="Basic Information" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Partner Product ID *"
              value={formData.partner_product_id?.toString() || ''}
              onChange={(val) => handleChange('partner_product_id', val ? parseInt(val, 10) : 0)}
              type="number"
              placeholder="Unique identifier (e.g., 500)"
              hint="Must be a unique ID not already in use"
            />
            <Input
              label="Partner ID"
              value={formData.partner_id?.toString() || ''}
              onChange={(val) => handleChange('partner_id', val ? parseInt(val, 10) : null)}
              type="number"
              placeholder="Optional partner identifier"
            />
            <Input
              label="Partner Name *"
              value={formData.partner_name}
              onChange={(val) => handleChange('partner_name', val)}
              placeholder="Official partner name"
            />
            <Input
              label="Doing Business As *"
              value={formData.doing_business_as}
              onChange={(val) => handleChange('doing_business_as', val)}
              placeholder="DBA / Display name"
            />
            <Select
              label="Partner Type *"
              value={formData.partner_type}
              onChange={(val) => handleChange('partner_type', val as PartnerType)}
              options={PARTNER_TYPE_OPTIONS.filter((o) => o.value !== '').map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <Input
              label="Program Code"
              value={formData.program_code || ''}
              onChange={(val) => handleChange('program_code', val || null)}
              placeholder="InComm program code"
              hint="Optional InComm program code for this partner"
            />
            <Input
              label="Package ID"
              value={formData.package_id || ''}
              onChange={(val) => handleChange('package_id', val || null)}
              placeholder="InComm package ID"
              hint="Optional InComm package ID for this partner"
            />
            <Input
              label="Purse Name"
              value={formData.purse_name || ''}
              onChange={(val) => handleChange('purse_name', val || null)}
              placeholder="InComm purse name"
              hint="InComm purse/benefit identifier (max 20 chars)"
            />
            <div className="checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active ?? true}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span>Active</span>
              </label>
              <span className="field-hint">
                Whether this partner configuration is currently active.
              </span>
            </div>
            {formData.is_active === false && (
              <>
                <Input
                  type="datetime-local"
                  label="Scheduled Activation"
                  value={schedActivationLocal}
                  onChange={setSchedActivationLocal}
                  hint="Optional. When set, this partner will automatically activate at the specified time."
                />
                <Select
                  label="Timezone"
                  value={schedActivationTimezone}
                  onChange={(val) => setSchedActivationTimezone(val)}
                  options={TIMEZONE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </>
            )}
            <div className="checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={(formData.config as PartnerConfigJson | undefined)?.fulfillment_enabled ?? true}
                  onChange={(e) => handleChange('config', {
                    ...((formData.config as PartnerConfigJson | undefined) ?? { fulfillment_enabled: true }),
                    fulfillment_enabled: e.target.checked,
                  })}
                />
                <span>Fulfillment Enabled</span>
              </label>
              <span className="field-hint">
                When disabled, reward redemptions are created but never exported for processing.
                Use for demo/sales accounts that should not trigger real fulfillments.
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Effective Period */}
      <Card className="form-card">
        <CardHeader title="Effective Period" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Effective Date *"
              value={formData.effective_date}
              onChange={(val) => handleChange('effective_date', val)}
              type="date"
              hint="When this configuration becomes effective"
            />
            <Input
              label="Expiration Date"
              value={formData.expiration_date || ''}
              onChange={(val) => handleChange('expiration_date', val || null)}
              type="date"
              placeholder="Optional"
              hint="When this configuration expires (leave empty for no expiration)"
            />
          </div>
        </CardBody>
      </Card>

      {/* Default Offers */}
      <Card className="form-card">
        <CardHeader
          title="Default Offers"
          subtitle="Rewards automatically assigned to new users when they first access the catalog"
        />
        <CardBody>
          <DefaultOffersEditor
            value={formData.default_offers || null}
            onChange={handleDefaultOffersChange}
          />
        </CardBody>
      </Card>

      {/* Notes */}
      <Card className="form-card">
        <CardHeader title="Notes" />
        <CardBody>
          <TextArea
            label="Notes"
            value={formData.notes || ''}
            onChange={(val) => handleChange('notes', val || null)}
            placeholder="Optional notes about this partner configuration"
            rows={4}
          />
        </CardBody>
      </Card>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
          Cancel
        </button>
        <button className="btn-create" onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create Partner'}
        </button>
      </div>
    </div>
  );
};

export default PartnerCreate;
